import { useEffect, useRef, useState } from 'react';
import AdUnit from './AdUnit';
import './PartnerBannerCarousel.css';

const AUTO_MS = 4500;

/**
 * One-box partner banner carousel — shows a single ad and auto-slides the rest.
 */
export default function PartnerBannerCarousel({ banners, ariaLabel = 'Partner advertisements' }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);

  const count = banners?.length || 0;

  useEffect(() => {
    setIndex(0);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, AUTO_MS);
    return () => clearInterval(id);
  }, [count, paused]);

  if (!count) return null;

  const go = (next) => {
    if (count <= 1) return;
    setIndex(((next % count) + count) % count);
  };

  const onTouchStart = (e) => {
    touchX.current = e.changedTouches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e) => {
    if (touchX.current == null || count <= 1) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 40) return;
    go(index + (dx < 0 ? 1 : -1));
  };

  return (
    <div
      className="partner-carousel"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="partner-carousel__viewport">
        <div
          className="partner-carousel__track"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {banners.map((ad, i) => (
            <div
              key={ad.id}
              className="partner-carousel__slide"
              aria-hidden={i !== index}
            >
              {Math.abs(i - index) <= 1 ? (
                <AdUnit ad={ad} variant="banner" active={i === index} />
              ) : (
                <div className="partner-carousel__placeholder" aria-hidden />
              )}
            </div>
          ))}
        </div>
      </div>

      {count > 1 && (
        <div className="partner-carousel__controls">
          <button
            type="button"
            className="partner-carousel__nav"
            aria-label="Previous advertisement"
            onClick={() => go(index - 1)}
          >
            ‹
          </button>
          <div className="partner-carousel__dots" role="tablist" aria-label="Advertisement slides">
            {banners.map((ad, i) => (
              <button
                key={ad.id}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`Advertisement ${i + 1}`}
                className={`partner-carousel__dot${i === index ? ' is-active' : ''}`}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
          <button
            type="button"
            className="partner-carousel__nav"
            aria-label="Next advertisement"
            onClick={() => go(index + 1)}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
